import { Box, Typography, Paper } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { PedidoCard } from './PedidoCard';
import { Pedido, StatusPedido } from '../../types/pedido';

interface StatusColumnProps {
  status: StatusPedido;
  title: string;
  pedidos: Pedido[];
  onPedidoClick: (pedido: Pedido) => void;
  onStatusChange: (pedidoId: string, novoStatus: StatusPedido) => void;
  onStatusUpdated?: () => void;
}

export const StatusColumn = ({
  status,
  title,
  pedidos,
  onPedidoClick,
  onStatusChange,
  onStatusUpdated,
}: StatusColumnProps) => {
  const { setNodeRef } = useDroppable({
    id: status,
    data: {
      accepts: [status],
    },
  });

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        height: '80vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          borderBottom: `2px solid ${getStatusColor(status)}`,
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{
            color: getStatusColor(status),
            fontWeight: 'medium',
            flexGrow: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 1 }}
        >
          ({pedidos.length})
        </Typography>
      </Box>

      <Box
        ref={setNodeRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          flex: 1,
          minHeight: 100,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
          },
        }}
      >
        {pedidos.map((pedido, index) => (
          <PedidoCard
            key={pedido.id}
            pedido={pedido}
            index={index}
            onClick={() => onPedidoClick(pedido)}
            onStatusUpdated={onStatusUpdated}
          />
        ))}
      </Box>
    </Paper>
  );
};

const getStatusColor = (status: StatusPedido): string => {
  // Cores personalizadas para melhor contraste e visibilidade
  const colors = {
    [StatusPedido.RECEBIDO]: '#000000', // Preto
    [StatusPedido.EM_PREPARO]: '#1a237e', // Azul escuro
    [StatusPedido.EM_ROTA]: '#e65100', // Laranja escuro
    [StatusPedido.PRONTO]: '#1b5e20', // Verde escuro
    [StatusPedido.ENTREGUE]: '#1b5e20', // Verde escuro
    [StatusPedido.CANCELADO]: '#1b5e20' // Verde escuro (mesmo dos finalizados)
  };
  return colors[status] || '#9e9e9e'; // Cinza como fallback
};
